// src/components/dashboard/cases/newCase/case_id/case.js (complete file)
import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AccountLabel from "./accountLabel";
import PatientLabel from "./patientLabel";
import ProviderLabel from "./providerLabel";
import AppointmentLabel from "./appointmentLabel";
import ColorLabel from "./colorLabel";
import ShipmentLabel from "./shipmentLabel";
import StatusLabel from "./statusLabel";
import TagLabel from "./tagLabel";
import CaseTab from "./caseTab";
import Production from "./Production";
import Shipping from "./shippingSection";
import axios from "axios";
import ActionButton from "./actionButton/button";
import CloseInvoiceCase from "../Invoice";

// Function to get user data from localStorage
const getUserData = () => {
  try {
    // Try different possible keys in localStorage
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];
    
    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          
          // Check for userRole in different possible locations
          const userRole = 
            parsed.userRole || 
            parsed.role || 
            parsed.data?.userRole || 
            parsed.data?.role ||
            parsed.user?.userRole ||
            parsed.user?.role;
          
          const userId = 
            parsed.userId || 
            parsed.id || 
            parsed.data?.userId || 
            parsed.data?.id ||
            parsed.user?.userId ||
            parsed.user?.id;
          
          if (userRole || userId) {
            console.log(`Found user data in key "${key}":`, { userRole, userId });
            return {
              userRole: userRole,
              userId: userId
            };
          }
        } catch (e) {
          console.warn(`Could not parse localStorage key "${key}":`, e);
          continue;
        }
      }
    }
    
    console.error('User data not found in localStorage');
    return { userRole: null, userId: null };
  } catch (error) {
    console.error('Error getting user data from localStorage:', error);
    return { userRole: null, userId: null };
  }
};

// Function to get userLabId from localStorage
const getUserLabId = () => {
  try {
    // Try different possible keys in localStorage
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];
    
    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          
          // Check for userLabId in different possible locations
          const userLabId = 
            parsed.userLabId || 
            parsed.labId || 
            parsed.id || 
            (parsed.data && parsed.data.userLabId) ||
            (parsed.data && parsed.data.labId) ||
            (parsed.user && parsed.user.userLabId) ||
            (parsed.user && parsed.user.labId);
          
          if (userLabId) {
            console.log(`Found userLabId in key "${key}":`, userLabId);
            return userLabId;
          }
          
          // Check if the value itself is the labId
          if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
            console.log('Found labId as direct string:', storedData);
            return storedData;
          }
        } catch (e) {
          console.warn(`Could not parse localStorage key "${key}":`, e);
          continue;
        }
      }
    }
    
    // If not found in structured data, look for raw labId string
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      if (value && value.match(/^000000\d{2}$/)) {
        console.log('Found labId as raw value in key:', key, value);
        return value;
      }
    }
    
    console.error('User labId not found in localStorage');
    return null;
  } catch (error) {
    console.error('Error getting userLabId from localStorage:', error);
    return null;
  }
};

const Case = () => {
  const [caseData, setCaseData] = useState(null);
  const { caseId } = useParams(); // caseId is assumed to be caseNumber (e.g., "30002")
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [visiblePages, setVisiblePages] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, "0") : "N/A";
  };

  // Set isAdmin based on userRole and fetch user permissions if not admin
  useEffect(() => {
    const userData = getUserData();
    const adminStatus = userData.userRole === "Admin" || userData.userRole === "admin";
    
    setUserRole(userData.userRole);
    setIsAdmin(adminStatus);

    if (!adminStatus && userData.userId) {
      const fetchPermissions = async () => {
        try {
          const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${userData.userId}/pages`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });
          if (!response.ok) {
            throw new Error('Failed to fetch permissions');
          }
          const data = await response.json();
          
          if (data && Array.isArray(data)) {
            // Extract page names from permissions
            const pageNames = data.map((perm) => perm.page?.name || perm.pageName).filter(Boolean);
            setVisiblePages(pageNames);
          }
        } catch (error) {
          console.error('Error fetching permissions:', error);
        }
      };
      fetchPermissions();
    } else if (adminStatus) {
      // If admin, we don't need to fetch permissions - they can see everything
      // You can optionally fetch all pages here if needed
    }
  }, []);

  // Helper function to check if a page should be visible
  const shouldShowPage = (pageName) => {
    if (isAdmin) {
      return true; // Admin can see all pages
    }
    return visiblePages.includes(pageName);
  };

  // Fetch accounts data based on userLabId
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const userLabId = getUserLabId();
        
        // If no userLabId, fetch all accounts (for admin or fallback)
        if (!userLabId) {
          console.warn('No userLabId found in localStorage, fetching all accounts');
          const response = await axios.get(
            "https://hms.backend.hiragroupcloud.com/api/account/account"
          );
          const accountsData = response.data.data;
          const formattedAccounts = Array.isArray(accountsData)
            ? accountsData.map((account) => ({
                _id: account._id,
                name: account.account_name,
                accountId: account.accountId,
                account_type: account.account_type,
                city: account.city,
                zip: account.zip,
                address: account.address,
                telephone: account.telephone,
                address2: account.address2,
                email: account.email,
                country: account.country,
                website: account.website,
                state: account.state,
                time_zone: account.time_zone,
                status: account.status,
                ddxEnable: account.ddxEnable,
                addedBy: account.addedBy,
                createdAt: account.createdAt,
                updatedAt: account.updatedAt,
              }))
            : [];
          setAccounts(formattedAccounts);
          return;
        }

        // Fetch only accounts for this specific lab
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`
        );
        
        if (response.data.success) {
          const accountsData = response.data.data;
          const formattedAccounts = Array.isArray(accountsData)
            ? accountsData.map((account) => ({
                _id: account._id,
                name: account.account_name,
                accountId: account.accountId,
                account_type: account.account_type,
                city: account.city,
                zip: account.zip,
                address: account.address,
                telephone: account.telephone,
                address2: account.address2,
                email: account.email,
                country: account.country,
                website: account.website,
                state: account.state,
                time_zone: account.time_zone,
                status: account.status,
                ddxEnable: account.ddxEnable,
                addedBy: account.addedBy,
                createdAt: account.createdAt,
                updatedAt: account.updatedAt,
              }))
            : [];
          setAccounts(formattedAccounts);
        } else {
          console.error('Failed to fetch lab accounts:', response.data.message);
          setAccounts([]);
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setAccounts([]);
      }
    };

    fetchAccounts();
  }, []);

  // Fetch case details by caseNumber
  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`
        );
        
        setCaseData(response.data);
      } catch (err) {
        setError(
          "Error fetching case details: " +
            (err.response?.data?.message || err.message)
        );
      } finally {
        setLoading(false);
      }
    };
    fetchCaseDetails();
  }, [caseId]);

  // Fetch account details when caseData is available
  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!caseData?.account_holder) return;

      try {
        // Try to find by accountId if account_holder is an ID
        if (
          typeof caseData.account_holder === "string" &&
          !isNaN(caseData.account_holder)
        ) {
          const response = await axios.get(
            `https://hms.backend.hiragroupcloud.com/api/account/account/${caseData.account_holder}`
          );
          setAccountDetails(response.data);
          return;
        }

        // If not found by ID, try to find by name in the filtered accounts
        const account = accounts.find(
          (acc) =>
            acc.accountId === caseData.account_holder ||
            acc.name?.toLowerCase() ===
              caseData.account_holder?.toLowerCase() ||
            acc.account_name?.toLowerCase() ===
              caseData.account_holder?.toLowerCase()
        );

        if (account) {
          const response = await axios.get(
            `https://hms.backend.hiragroupcloud.com/api/account/account/${account.accountId}`
          );
          setAccountDetails(response.data);
        }
      } catch (error) {
        console.error("Error fetching account details:", error);
      }
    };

    fetchAccountDetails();
  }, [caseData, accounts]);

  // Fetch invoice data only if caseData has invoiceNumber
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!caseData?.invoiceNumber) return;
      try {
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/invoices?caseId=${caseData.caseNumber}`
        );
        if (response.data.length > 0) {
          setInvoiceData(response.data[0]);
        }
      } catch (err) {
        console.error("Error fetching invoice data:", err);
      }
    };
    fetchInvoiceData();
  }, [caseData]);

  const handleAccountClick = () => {
    if (!accountDetails) {
      console.error("No account details available");
      alert("No account details available for this case");
      return;
    }

    navigate(`/tabsWorking/${accountDetails.accountId}`, {
      state: {
        accountData: accountDetails,
      },
    });
  };

  const handleInvoiceCreated = (newInvoice) => {
    setInvoiceData(newInvoice);
    setCaseData((prev) => ({
      ...prev,
      invoiceNumber: newInvoice.invoiceNumber,
    }));
  };

  const handleTryInUpdate = (newTryInStatus) => {
    setCaseData((prev) => ({
      ...prev,
      try_in: newTryInStatus,
    }));
  };

  const handleUpdate = (updatedCase) => {
    setCaseData(updatedCase);
  };

  // Check access using helper function
  const hasInvoiceAccess = shouldShowPage('Invoice Details');
  const hasCloseInvoiceAccess = shouldShowPage('Close/Invoice Case');

  if (loading) return <p>Loading case details...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  const patientFullName =
    `${caseData?.first_name || ""} ${caseData?.last_name || ""}`.trim() ||
    "No Patient Name";

  return (
    <div>


      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "95%",
          margin: "20px auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <h2 style={{ fontSize: "36px" }}>Case #{caseId}</h2>
          {caseData?.invoiceNumber && (
            <div>
              <h2 style={{ fontSize: "36px" }}>
                Invoice{" "}
                {hasInvoiceAccess ? (
                  <Link
                    to={`/invoices/${formatInvoiceNumber(
                      caseData.invoiceNumber
                    )}`}
                    state={{ invoiceData, caseId: caseData?.caseNumber }}
                  >
                    #{formatInvoiceNumber(caseData.invoiceNumber)}
                  </Link>
                ) : (
                  <span>#{formatInvoiceNumber(caseData.invoiceNumber)}</span>
                )}
              </h2>
            </div>
          )}
          {caseData?.status === "canceled" && (
            <div
              style={{
               marginTop:"15px",
                position: "relative",
                display: "inline-block",
                marginLeft: "20px",
              }}
            >
              <div
                style={{
                  fontSize: "36px",
                  color: "red",
                  fontWeight: "bold",
                  transform: "rotate(-15deg)",
                  border: "2px solid red",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  backgroundColor: "rgba(255, 0, 0, 0.1)",
                }}
              >
                CANCELLED
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {!caseData?.invoiceNumber && hasCloseInvoiceAccess && (
            <CloseInvoiceCase
              caseData={caseData}
              onInvoiceCreated={handleInvoiceCreated}
            />
          )}
          <ActionButton
            caseId={caseData?.case_id}
            caseNumber={caseId}
            initialArrived={!!caseData?.arrive_date}
            onTryInUpdate={handleTryInUpdate}
            onStatusUpdate={(updatedStatus) => {
              setCaseData((prev) => ({
                ...prev,
                status: updatedStatus,
              }));
            }}
          />
        </div>
      </div>

      <div
        style={{
          marginTop: "1%",
          width: "95%",
          margin: "0 auto",
          padding: "0px",
        }}
      >
        <div className="row" style={{ width: "100%" }}>
          <div className="col-lg-5">
            <div
              onClick={handleAccountClick}
              style={{
                cursor: "pointer",
              }}
            >
              <AccountLabel
                account={accountDetails?.account_name || "No Account Available"}
              />
            </div>
          </div>
          <div className="col-lg-5" style={{ marginLeft: "8%" }}>
            <PatientLabel patientName={patientFullName} caseId={caseId} />
          </div>
        </div>

        <div className="row" style={{ width: "100%" }}>
          <div className="col-lg-6">
            <ProviderLabel
              caseNumber={caseId}
              providerName={caseData?.provider_name || "No provider assigned"}
              onProviderChange={(newName) => {
                setCaseData(prev => ({ ...prev, provider_name: newName }));
              }}
            />
          </div>
          <div className="col-lg-6">
            <AppointmentLabel
              caseId={caseId}
              initialAppointment={caseData?.appointment}
            />
          </div>
        </div>

        <div
          className="row"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
          }}
        >
          <div
            className="col-lg-6"
            style={{
              width: "350px",
              minWidth: "250px",
              maxWidth: "400px",
              marginRight: "20px",
            }}
          >
            <ColorLabel
              panColor={caseData?.color || "No Color Info"}
              panNumber={caseData?.pan_number || ""}
              caseId={caseId}
              onUpdate={handleUpdate}
            />
          </div>

          <div className="col-lg-6" style={{ marginRight: "10px" }}>
            <ShipmentLabel caseId={caseId} />
          </div>
        </div>

        <div className="row" style={{ width: "100%" }}>
          <div className="col-lg-6">
            <StatusLabel caseId={caseId} />
            {caseData?.status === "canceled" && caseData?.cancellationReason && (
              <div style={{ marginTop: "10px", color: "red" }}>
                Cancellation Reason: {caseData.cancellationReason}
              </div>
            )}
          </div>
          <div className="col-lg-6">
            <TagLabel
              caseId={caseId}
              tag={caseData?.tags || "No tags available"}
            />
          </div>
        </div>

        <div className="row" style={{ width: "100%" }}>
          <div className="col-lg-6">
            <Production caseId={caseId} />
          </div>
          <div className="col-lg-6">
            <Shipping caseId={caseId} />
          </div>
        </div>

        {caseData?.try_in && (
          <div className="row" style={{ width: "100%", marginTop: "10px" }}>
            <div className="col-lg-6">
              <div style={{ padding: '5px', borderRadius: '5px' }}>
                <span style={{ fontWeight: "bold" }}>Try-in</span>: Yes
              </div>
            </div>
          </div>
        )}

        <div className="row mt-3">
          <div className="col-lg-12">
            <CaseTab
              components={caseData?.components || "No components available"}
              caseId={caseId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Case;